import { forwardRef } from 'react';
import {
  BECOME_LOGO_SRC,
  OPL_LOGO_SRC,
  RINA_LOGO_SRC,
} from './courseCertificationDetails';
import {
  FCS_ECM_CREDITS_LEGAL_REF,
  FCS_TRAINING_OBJECTIVE_PARAGRAPH,
} from './fcsCertificateBoilerplate';
import { stripEcmCatalogPrefix } from '../utils/stripEcmCatalogPrefix';
import './formazione-continua-sanita.css';

export type ProCourseFormazioneContinuaSanitaCertificateProps = {
  courseName: string;
  /** Data in cui si è tenuto il corso (testo gg/mm/aaaa) */
  courseHeldDateLabel: string;
  article: 'il' | 'la';
  honorific: 'Dott.' | 'Dott.ssa';
  participantGivenName: string;
  participantFamilyName: string;
  fiscalCode: string;
  /** Data di partecipazione (gg/mm/aaaa) */
  participationDateLabel: string;
  /** Etichetta crediti ECM (es. numero già formattato + testo) */
  ecmCreditsLabel: string;
  /** Es. &quot;5 Psicologo&quot; */
  professionLine: string;
  /** Es. &quot;77 Psicoterapia&quot; */
  disciplineLine: string;
  /** Es. &quot;Milano, lì gg/mm/aaaa&quot; */
  issuerCityDateLabel: string;
  /** Numero evento formativo (testo libero) */
  eventNumber?: string;
  editionNumber?: string;
};

export const ProCourseFormazioneContinuaSanitaCertificate = forwardRef<
  HTMLDivElement,
  ProCourseFormazioneContinuaSanitaCertificateProps
>(function ProCourseFormazioneContinuaSanitaCertificate(
  {
    courseName,
    courseHeldDateLabel,
    article,
    honorific,
    participantGivenName,
    participantFamilyName,
    fiscalCode,
    participationDateLabel,
    ecmCreditsLabel,
    professionLine,
    disciplineLine,
    issuerCityDateLabel,
    eventNumber = '375106',
    editionNumber = '1',
  },
  ref
) {
  const fiscalTrimmed = fiscalCode.trim();
  const fiscalDisplay =
    fiscalTrimmed.length > 0 ? fiscalTrimmed.toUpperCase() : '—';
  const participantNameLine = `${participantGivenName} ${participantFamilyName}`;
  const professionDisplay = stripEcmCatalogPrefix(professionLine);
  const disciplineDisplay = stripEcmCatalogPrefix(disciplineLine);

  return (
    <div ref={ref} className='fcs-cert' data-certificate-export-root>
      <div className='fcs-cert__frame' aria-hidden>
        <div className='fcs-cert__frame-edge fcs-cert__frame-edge--top certificate-print-color-exact' />
        <div className='fcs-cert__frame-edge fcs-cert__frame-edge--right certificate-print-color-exact' />
        <div className='fcs-cert__frame-edge fcs-cert__frame-edge--bottom certificate-print-color-exact' />
        <div className='fcs-cert__frame-edge fcs-cert__frame-edge--left certificate-print-color-exact' />
      </div>

      <div className='fcs-cert__inner'>
        <header className='fcs-cert__header'>
          <div className='fcs-cert__header-logo-opl'>
            <img src={OPL_LOGO_SRC} alt='' crossOrigin='anonymous' />
          </div>
          <div className='fcs-cert__header-logo-rina'>
            <img src={RINA_LOGO_SRC} alt='' crossOrigin='anonymous' />
          </div>
        </header>

        <div className='fcs-cert__main'>
          <h1 className='fcs-cert__title'>
            Programma per la formazione continua dei professionisti della sanità
          </h1>

          <div className='fcs-cert__body'>
            <p className='fcs-cert__body-lead'>
              La Commissione Nazionale per la Formazione Continua nell’ambito
              della salute ha accreditato, con provvedimento istituzionale n.
              4983, l’Ordine degli Psicologi della Lombardia in qualità di
              provider. Lo stesso Provider ha organizzato l’evento formativo ai
              sensi del Regolamento ECM n. {eventNumber}, edizione n.{' '}
              {editionNumber}.
            </p>

            <p className='fcs-cert__course-title'>{courseName}</p>

            <p>
              tenutosi il{' '}
              <span className='fcs-cert__strong'>{courseHeldDateLabel}</span>,
              avente come obiettivo formativo
            </p>

            <p className='fcs-cert__objective'>
              {FCS_TRAINING_OBJECTIVE_PARAGRAPH}
            </p>

            <p className='fcs-cert__representative'>
              la sottoscritta Laura Parolin, Rappresentante legale del Provider
            </p>

            <p className='fcs-cert__attesta'>ATTESTA CHE</p>

            <p className='fcs-cert__participant'>
              <span className='fcs-cert__participant-title mr-dafoe-regular'>
                {honorific}
              </span>
              <span className='fcs-cert__participant-name mr-dafoe-regular'>
                {participantNameLine}
              </span>
            </p>

            <p className='fcs-cert__fiscal'>
              C.F. <span className='fcs-cert__strong'>{fiscalDisplay}</span>
            </p>

            <p>
              in qualità di partecipante {article}{' '}
              <span className='fcs-cert__strong'>{participationDateLabel}</span>{' '}
              ha acquisito:
            </p>

            <p className='fcs-cert__credits-line'>
              <span className='fcs-cert__strong'>{ecmCreditsLabel}</span>{' '}
              Crediti formativi E.C.M.{' '}
              <span className='fcs-cert__legal-ref'>
                {FCS_ECM_CREDITS_LEGAL_REF}
              </span>
            </p>

            <p>
              nella professione{' '}
              <span className='fcs-cert__strong'>{professionDisplay}</span>,
              disciplina{' '}
              <span className='fcs-cert__strong'>{disciplineDisplay}</span>.
            </p>
          </div>
        </div>

        <footer className='fcs-cert__footer'>
          <div className='fcs-cert__footer-become'>
            <img src={BECOME_LOGO_SRC} alt='' crossOrigin='anonymous' />
          </div>
          <div className='fcs-cert__footer-sign'>
            <div>
              <span className='fcs-cert__strong'>{issuerCityDateLabel}</span>
            </div>
            <div>IL RAPPRESENTANTE LEGALE</div>
            <div className='fcs-cert__footer-sign-name'>Laura Parolin</div>
            <div className='fcs-cert__footer-legal'>
              (Firma omessa ex art. 3 del D.Lgs. n. 39/1993)
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
});
